import React, { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Line } from 'react-konva';
import { useTranslation } from 'react-i18next';
import tireImageSrc from '../../../images/pneumatico.png';
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/graniglia-pav.png';
import texture4Image from '../../../images/subgrade.png';

interface Props {
    layerHeights?: number[]; // Array con altezze dei 3 strati (Surface, Base, Subgrade)
    differenziali?: number[]; // Array con i valori differenziali (opzionale)
    projectType?: 'container_yard' | 'parking_deck' | string;
}

// Valori di default per le props (Base, Subgrade)
const defaultLayerHeights = [300, 120];
const defaultDifferenziali = [0, 0];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TireAndLayers: React.FC<Props> = ({ layerHeights = defaultLayerHeights, differenziali = defaultDifferenziali, projectType = 'container_yard' }) => {
    const { t } = useTranslation();
    // Altezza fissa per asfalto
    const ASPHALT_HEIGHT = 20;
    // Base e subgrade dai dati
    const safeLayerHeights = [layerHeights[0] ?? 0, layerHeights[1] ?? 0];
    const layersTotalHeight = ASPHALT_HEIGHT + safeLayerHeights.reduce((acc, h) => acc + h, 0);
    const squareWidth = 400; // Larghezza dei rettangoli
    // --- Image constants and dynamic selection ---
    const IMAGE_TOP_PADDING = 2;
    const TIRE_RAPPORTO_DIAGONALE = 2.2; // 350 / 150
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;

    // In Pavements module, always use tire image regardless of projectType
    const imageSrcToLoad = tireImageSrc; // Always use tire image in Pavements
    const currentImageHeight = TIRE_IMAGE_HEIGHT; // Always use tire dimensions in Pavements
    const currentImageWidth = TIRE_IMAGE_WIDTH; // Always use tire dimensions in Pavements
    // --- NUOVE COSTANTI PER IL LAYOUT ---
    const titleAreaWidth = 160; // Spazio dedicato ai titoli a sinistra
    const rectStartX = titleAreaWidth; // Inizio dei rettangoli
    const arrowLength = 60; // Lunghezza della freccia
    const arrowGap = 10; // Spazio tra rettangolo e inizio freccia
    const labelGap = 10; // Spazio tra fine freccia ed etichetta 'd'
    const stageWidth = rectStartX + squareWidth + arrowGap + arrowLength + labelGap + 60; // Larghezza totale calcolata + margine
    const stageHeight = IMAGE_TOP_PADDING + currentImageHeight + layersTotalHeight + 10; /* bottom padding */

    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [texture1Img, setTexture1Img] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        const img = new window.Image();
        img.src = imageSrcToLoad; // Use dynamic image source
        img.onload = () => setImage(img);
        img.onerror = () => {};

        const textures = [texture1Image, texture2Image, texture4Image];
        const setters = [setTexture1Img, setTexture2Img, setTexture4Img];

        textures.forEach((src, index) => {
            const tex = new window.Image();
            tex.src = src;
            tex.onload = () => setters[index](tex);
            tex.onerror = e => {};
        });
    }, [imageSrcToLoad]);

    return (
        <Stage width={stageWidth} height={stageHeight} x={-50}>
            <Layer>
                {/* Strato asfalto fisso in cima */}
                <Text text="Surface Layer" x={5} y={IMAGE_TOP_PADDING + currentImageHeight + ASPHALT_HEIGHT / 2 - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />
                <Rect
                    x={rectStartX}
                    y={IMAGE_TOP_PADDING + currentImageHeight}
                    width={squareWidth}
                    height={ASPHALT_HEIGHT}
                    fillPatternImage={texture1Img || undefined}
                    fillPatternRepeat="repeat"
                    fillPatternScale={{ x: 0.2, y: 0.2 }}
                    stroke="grey"
                    strokeWidth={0.5}
                />
                {/* Bordo superiore asfalto */}
                {/* <Rect
                    x={rectStartX}
                    y={IMAGE_TOP_PADDING + currentImageHeight}
                    width={squareWidth}
                    height={3}
                    fill="#e4cab7"
                    listening={false}
                /> */}

                {/* Base Course e Subgrade sotto l'asfalto */}
                {safeLayerHeights.map((height, index) => {
                    if (!height || height <= 0) return null;
                    // Y parte da sotto l'asfalto
                    const rectY = IMAGE_TOP_PADDING + currentImageHeight + ASPHALT_HEIGHT + safeLayerHeights.slice(0, index).reduce((acc, h) => acc + h, 0);

                    // Se è il Base Course (index === 0), dividi in due metà
                    if (index === 0) {
                        const halfHeight = height / 2;
                        const topY = rectY;
                        const bottomY = rectY + halfHeight;
                        const topCenterY = topY + halfHeight / 2;
                        const bottomCenterY = bottomY + halfHeight / 2;

                        return (
                            <React.Fragment key={index}>
                                {/* Base Layer (top half) */}
                                <Text
                                    text={t('base_layer') as string}
                                    x={5}
                                    y={topCenterY - 6}
                                    width={titleAreaWidth - 10}
                                    wrap="word"
                                    align="right"
                                    fontSize={12}
                                    fill="black"
                                />
                                <Rect
                                    x={rectStartX}
                                    y={topY}
                                    width={squareWidth}
                                    height={halfHeight}
                                    fillPatternImage={texture2Img || undefined}
                                    fillPatternRepeat="repeat-y"
                                    fillPatternScale={{ x: 1.0, y: 1.0 }}
                                    stroke="grey"
                                    strokeWidth={0.5}
                                />

                                {/* Dividing Line */}
                                <Line
                                    points={[rectStartX, bottomY, rectStartX + squareWidth, bottomY]}
                                    stroke="black"
                                    strokeWidth={2}
                                />

                                {/* Subbase Layer (bottom half) */}
                                <Text
                                    text={t('subbase_layer') as string}
                                    x={5}
                                    y={bottomCenterY - 6}
                                    width={titleAreaWidth - 10}
                                    wrap="word"
                                    align="right"
                                    fontSize={12}
                                    fill="black"
                                />
                                <Rect
                                    x={rectStartX}
                                    y={bottomY}
                                    width={squareWidth}
                                    height={halfHeight}
                                    fillPatternImage={texture2Img || undefined}
                                    fillPatternRepeat="repeat-y"
                                    fillPatternScale={{ x: 0.8, y: 0.8 }}
                                    stroke="grey"
                                    strokeWidth={0.5}
                                />
                            </React.Fragment>
                        );
                    }

                    // Altrimenti (Subgrade), rendering normale
                    const centerY = rectY + height / 2;
                    const currentTexture = texture4Img;
                    const currentTitle = 'Subgrade';
                    const fillPatternRepeat: 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat' = 'repeat';
                    const textureScale = { x: 0.5, y: 0.5 };

                    return (
                        <React.Fragment key={index}>
                            <Text text={currentTitle} x={5} y={centerY - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />
                            <Rect
                                x={rectStartX}
                                y={rectY}
                                width={squareWidth}
                                height={height}
                                fillPatternImage={currentTexture || undefined}
                                fillPatternRepeat={fillPatternRepeat}
                                fillPatternScale={textureScale}
                                stroke="grey"
                                strokeWidth={0.5}
                            />
                        </React.Fragment>
                    );
                })}

                {/* Immagine Pneumatico */}
                {image && (
                    <KonvaImage
                        image={image}
                        x={rectStartX + squareWidth / 2 - currentImageWidth / 2} // Centro esatto sopra il rettangolo
                        y={IMAGE_TOP_PADDING}
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}
            </Layer>
        </Stage>
    );
};

export default TireAndLayers;
